//Libs
import {
  Fragment,
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  ColDef,
  ICellRendererParams,
  RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

//Local
import { ExistingZone, Zone } from "../../models/zone";
import * as strings from "../../strings";
import HeadingOne from "../../components/HeadingOne";

interface Props {
  //DATA
  zoneList: Zone[];
  addNewZoneButton: React.ReactNode;
  //FUNCTIONS
  onEditZone: (zone: ExistingZone) => void;
}

export default function ZoneListPage({
  zoneList,
  addNewZoneButton,
  onEditZone,
}: Props) {
  return (
    <div>
      <HeadingOne>{strings.ZONES}</HeadingOne>
      <div className={"ag-theme-alpine my-4 flex h-[600px] flex-col pb-14"}>
        <ZoneListTable
          zoneList={zoneList}
          addNewZoneButton={addNewZoneButton}
          onEditZone={onEditZone}
        />
      </div>
    </div>
  );
}

const headerStyles =
  "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase";

const commonColProps = {
  minWidth: 150,
  flex: 1,
  resizable: true,
  filter: true,
  sortable: true,
  headerClass: headerStyles,
};

interface ZoneListTableProps {
  zoneList: Props["zoneList"];
  addNewZoneButton: Props["addNewZoneButton"];
  onEditZone: Props["onEditZone"];
}

/* TABLE COMPONENT */
const ZoneListTable = memo(
  ({ zoneList, addNewZoneButton, onEditZone }: ZoneListTableProps) => {
    const [gridReady, setGridReady] = useState(false);
    const gridRef = useRef<any>(null);

    const onGridReady = useCallback(() => {
      setGridReady(true);
    }, []);

    useLayoutEffect(() => {
      if (gridRef.current && gridReady) {
        gridRef.current.api?.sizeColumnsToFit();
      }
    }, [gridReady]);

    const columnDefs: ColDef[] = [
      {
        field: "color",
        headerName: "",
        maxWidth: 80,
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "2",
        },
        cellRenderer: (params: ICellRendererParams) => {
          const color = params.value as string;
          return (
            <div
              className="h-6 w-6 rounded-full"
              style={{ background: color }}
            />
          );
        },
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        field: "name",
        maxWidth: 350,
        headerName: "Name",
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        field: "values",
        headerName: "Values",
        cellRenderer: (params: ICellRendererParams) => {
          const values = params.value as string[];
          return values.join(", ");
        },
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        field: "property",
        headerName: "Property",
        cellRenderer: (params: ICellRendererParams) => {
          const property = params.value as string;
          if (property === "zipCode") {
            return "Zip Code";
          }
          if (property === "title-contains") {
            return "Task Title Contains";
          }
          return property;
        },
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
      {
        field: "element",
        headerName: "Element",
        cellRenderer: (params: ICellRendererParams) => {
          const element = params.value as string;
          if (element === "dot") {
            return "Dot";
          }
          if (element === "background") {
            return "Background";
          }
          return element;
        },
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      },
    ];

    return (
      <Fragment>
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end sm:justify-end">
          {addNewZoneButton}
        </div>
        <AgGridReact
          ref={gridRef}
          onGridReady={onGridReady}
          defaultColDef={commonColProps}
          className="mt-5 shadow"
          rowData={zoneList}
          animateRows={true}
          rowSelection="single"
          rowHeight={50}
          cacheQuickFilter={true}
          columnDefs={columnDefs}
          onRowClicked={(event: RowClickedEvent) => onEditZone(event.data)}
        />
      </Fragment>
    );
  },
);
